import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  useToast,
  Grid,
  GridItem,
  IconButton,
  Tooltip,
  Badge,
  useColorModeValue,
  Flex,
  Center,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon } from '@chakra-ui/icons';
import supabase from '../supabaseClient';

// Helper function to get date string in local timezone (YYYY-MM-DD format)
const getLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get the Monday of the week for a given date
const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  return new Date(d.setDate(diff));
};

// Generate time slots from 8am to 6pm
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour < 18; hour++) {
    const startTime = `${hour}:00`; // No leading zero (e.g., "8:00" not "08:00")
    const endTime = `${hour + 1}:00`;
    slots.push({
      start: startTime,
      end: endTime,
      label: `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`,
    });
  }
  return slots;
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = generateTimeSlots();

const MakeupSessionAvailability = ({ therapistId }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getMonday(new Date()));
  const [availability, setAvailability] = useState({});
  const [assignedSessions, setAssignedSessions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const { isOpen: isAssignmentModalOpen, onOpen: onAssignmentModalOpen, onClose: onAssignmentModalClose } = useDisclosure();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('brand.500', 'brand.600');
  const closedBg = useColorModeValue('gray.100', 'gray.700');
  const openBg = useColorModeValue('green.100', 'green.800');
  const assignedBg = useColorModeValue('blue.100', 'blue.800');
  const pastBg = useColorModeValue('gray.50', 'gray.900');

  // Get dates for the current week
  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  // Check if a date/time is in the past
  const isPastSlot = (date, timeSlot) => {
    const now = new Date();
    const slotDateTime = new Date(date);
    const [hours] = timeSlot.start.split(':').map(Number);
    slotDateTime.setHours(hours, 0, 0, 0);
    return slotDateTime < now;
  };

  // Fetch availability from database
  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const startDate = getLocalDateString(weekDates[0]);
      const endDate = getLocalDateString(weekDates[6]);

      const { data, error } = await supabase
        .from('makeup_session_availability')
        .select('*')
        .eq('therapist_id', therapistId)
        .gte('date', startDate)
        .lte('date', endDate);

      if (error) throw error;

      // Convert to a map for easy lookup
      // true = open, false = closed, null = assigned
      const availabilityMap = {};
      data?.forEach((item) => {
        const key = `${item.date}_${item.time_slot}`;
        availabilityMap[key] = item.is_open; // Will be true, false, or null
      });

      setAvailability(availabilityMap);

      // Fetch assigned sessions for slots where is_open is null
      const assignedSlots = data?.filter(item => item.is_open === null) || [];
      if (assignedSlots.length > 0) {
        // Query sessions table to find the makeup sessions assigned to these slots
        const { data: sessionsData, error: sessionsError } = await supabase
          .from('sessions')
          .select(`
            id,
            valid_from,
            time,
            client_name,
            client:clients (
              id,
              name,
              nickname
            ),
            makeup_for
          `)
          .eq('therapist_id', therapistId)
          .eq('is_makeup', true)
          .in('valid_from', assignedSlots.map(s => s.date));

        if (!sessionsError && sessionsData) {
          const assignedMap = {};
          sessionsData.forEach(session => {
            // Match by date and time (use format without leading zero: "9:00" not "09:00")
            const sessionHour = parseInt(session.time.split(':')[0]);
            const timeSlot = `${sessionHour}:00`;
            const key = `${session.valid_from}_${timeSlot}`;
            assignedMap[key] = {
              clientName: session.client?.name || session.client_name,
              clientNickname: session.client?.nickname,
              makeupFor: session.makeup_for
            };
          });
          setAssignedSessions(assignedMap);
        }
      } else {
        setAssignedSessions({});
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast({
        title: 'Error loading availability',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (therapistId) {
      fetchAvailability();
    }
  }, [therapistId, currentWeekStart]);

  // Check if a day is Sunday
  const isSunday = (date) => {
    return date.getDay() === 0;
  };

  // Open a slot (one-way only - can't close once opened)
  const openSlot = async (date, timeSlot) => {
    const dateStr = getLocalDateString(date);
    const key = `${dateStr}_${timeSlot.start}`;
    const currentValue = availability[key] || false;

    // If already open, don't allow closing
    if (currentValue) {
      toast({
        title: 'Cannot close slot',
        description: 'Once a slot is opened, it cannot be closed. Please contact admin if needed.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Optimistically update UI
    setAvailability((prev) => ({
      ...prev,
      [key]: true,
    }));

    setSaving(true);
    try {
      const { error } = await supabase.from('makeup_session_availability').upsert(
        {
          therapist_id: therapistId,
          date: dateStr,
          time_slot: timeSlot.start,
          is_open: true,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'therapist_id,date,time_slot',
        }
      );

      if (error) throw error;

      toast({
        title: 'Slot opened',
        description: 'This slot is now available for makeup sessions.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      // Revert on error
      setAvailability((prev) => ({
        ...prev,
        [key]: currentValue,
      }));

      console.error('Error updating availability:', error);
      toast({
        title: 'Error updating slot',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Navigate weeks
  const goToPreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const goToCurrentWeek = () => {
    setCurrentWeekStart(getMonday(new Date()));
  };

  // Check if we're viewing the current week
  const isCurrentWeek = () => {
    const today = new Date();
    const currentMonday = getMonday(today);
    return getLocalDateString(currentMonday) === getLocalDateString(currentWeekStart);
  };

  // Format date for display
  const formatDateHeader = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Check if entire day is in the past
  const isDayInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  if (loading) {
    return (
      <Center py={10}>
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color="gray.500">Loading availability...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box>
      {/* Header with navigation */}
      <VStack spacing={4} mb={6}>
        <Heading size="lg" color="brand.600">
          Makeup Session Availability
        </Heading>
        <Text color="gray.500" textAlign="center">
          Open time slots available for makeup sessions
        </Text>
        <Box 
          bg="orange.50" 
          border="1px" 
          borderColor="orange.200" 
          borderRadius="md" 
          p={3} 
          maxW="500px"
        >
          <Text fontSize="sm" color="orange.700" textAlign="center">
            <strong>Note:</strong> Once a slot is opened, it cannot be closed. 
          </Text>
        </Box>

        <HStack spacing={4}>
          <IconButton
            icon={<ChevronLeftIcon boxSize={6} />}
            onClick={goToPreviousWeek}
            aria-label="Previous week"
            variant="outline"
            colorScheme="brand"
          />

          <VStack spacing={0}>
            <Text fontWeight="bold" fontSize="lg">
              {weekDates[0].toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {formatDateHeader(weekDates[0])} - {formatDateHeader(weekDates[6])}
            </Text>
          </VStack>

          <IconButton
            icon={<ChevronRightIcon boxSize={6} />}
            onClick={goToNextWeek}
            aria-label="Next week"
            variant="outline"
            colorScheme="brand"
          />
        </HStack>

        {!isCurrentWeek() && (
          <Button size="sm" variant="ghost" colorScheme="brand" onClick={goToCurrentWeek}>
            Go to Current Week
          </Button>
        )}
      </VStack>

      {/* Legend */}
      <HStack spacing={6} mb={4} justify="center" flexWrap="wrap">
        <HStack>
          <Box w={4} h={4} bg={closedBg} borderRadius="md" border="1px" borderColor={borderColor} />
          <Text fontSize="sm">Closed</Text>
        </HStack>
        <HStack>
          <Box w={4} h={4} bg={openBg} borderRadius="md" border="1px" borderColor="green.300" />
          <Text fontSize="sm">Open (locked)</Text>
        </HStack>
        <HStack>
          <Box w={4} h={4} bg={assignedBg} borderRadius="md" border="1px" borderColor="blue.300" />
          <Text fontSize="sm">Assigned</Text>
        </HStack>
        <HStack>
          <Box w={4} h={4} bg={pastBg} borderRadius="md" border="1px" borderColor={borderColor} opacity={0.5} />
          <Text fontSize="sm">Past</Text>
        </HStack>
      </HStack>

      {/* Calendar Grid */}
      <Box overflowX="auto" pb={4}>
        <Grid
          templateColumns={`80px repeat(7, minmax(100px, 1fr))`}
          gap={1}
          minW="850px"
          bg={bgColor}
          borderRadius="lg"
          border="1px"
          borderColor={borderColor}
          overflow="hidden"
        >
          {/* Header Row */}
          <GridItem bg={headerBg} p={2}>
            <Text color="white" fontWeight="bold" fontSize="sm" textAlign="center">
              Time
            </Text>
          </GridItem>
          {weekDates.map((date, idx) => {
            const isToday = getLocalDateString(date) === getLocalDateString(new Date());
            const isPast = isDayInPast(date);
            return (
              <GridItem
                key={idx}
                bg={isToday ? 'brand.600' : headerBg}
                p={2}
                opacity={isPast ? 0.7 : 1}
              >
                <VStack spacing={0}>
                  <Text color="white" fontWeight="bold" fontSize="sm">
                    {DAYS_OF_WEEK[idx].slice(0, 3)}
                  </Text>
                  <Text color="white" fontSize="xs">
                    {formatDateHeader(date)}
                  </Text>
                  {isToday ? (
                    <Badge colorScheme="yellow" fontSize="xs" mt={1}>
                      Today
                    </Badge>
                  ) : isSunday(date) ? (
                    <Badge colorScheme="orange" fontSize="xs" mt={1}>
                      Sunday
                    </Badge>
                  ) : null}
                </VStack>
              </GridItem>
            );
          })}

          {/* Time Slot Rows */}
          {TIME_SLOTS.map((slot, slotIdx) => (
            <React.Fragment key={slot.start}>
              {/* Time label */}
              <GridItem
                bg={slotIdx % 2 === 0 ? 'gray.50' : 'white'}
                p={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  {slot.label}
                </Text>
              </GridItem>

              {/* Day cells */}
              {weekDates.map((date, dayIdx) => {
                const dateStr = getLocalDateString(date);
                const key = `${dateStr}_${slot.start}`;
                const slotValue = availability[key];
                const isOpen = slotValue === true;
                const isAssigned = slotValue === null && availability.hasOwnProperty(key);
                const isPast = isPastSlot(date, slot);
                const isDisabled = isPast || isOpen || isAssigned || saving;
                const assignmentInfo = assignedSessions[key];

                const handleSlotClick = () => {
                  if (isAssigned && assignmentInfo) {
                    setSelectedAssignment({
                      ...assignmentInfo,
                      date: dateStr,
                      time: slot.label
                    });
                    onAssignmentModalOpen();
                  } else if (!isDisabled) {
                    openSlot(date, slot);
                  }
                };

                return (
                  <GridItem
                    key={`${slotIdx}-${dayIdx}`}
                    bg={isPast ? pastBg : isAssigned ? assignedBg : isOpen ? openBg : closedBg}
                    p={1}
                    opacity={isPast ? 0.5 : 1}
                  >
                    <Tooltip
                      label={
                        isPast
                          ? 'Past time slot'
                          : isAssigned
                          ? `Assigned to ${assignmentInfo?.clientName || 'a client'} - Click for details`
                          : isOpen
                          ? 'Slot is open (cannot be closed)'
                          : 'Click to open slot'
                      }
                      hasArrow
                    >
                      <Button
                        size="sm"
                        w="100%"
                        h="40px"
                        variant={isOpen || isAssigned ? 'solid' : 'outline'}
                        colorScheme={isAssigned ? 'blue' : isOpen ? 'green' : 'gray'}
                        isDisabled={isPast || saving}
                        onClick={handleSlotClick}
                        fontSize="xs"
                        cursor={isAssigned ? 'pointer' : isOpen ? 'not-allowed' : 'pointer'}
                        _disabled={{
                          opacity: isOpen ? 0.8 : 0.4,
                          cursor: isPast ? 'not-allowed' : (isAssigned ? 'pointer' : 'not-allowed'),
                        }}
                      >
                        {isPast ? '—' : isAssigned ? 'Assigned' : isOpen ? 'Open ✓' : 'Closed'}
                      </Button>
                    </Tooltip>
                  </GridItem>
                );
              })}
            </React.Fragment>
          ))}
        </Grid>
      </Box>

      {saving && (
        <Center mt={4}>
          <HStack>
            <Spinner size="sm" color="brand.500" />
            <Text fontSize="sm" color="gray.500">
              Saving...
            </Text>
          </HStack>
        </Center>
      )}

      {/* Assignment Details Modal */}
      <Modal isOpen={isAssignmentModalOpen} onClose={onAssignmentModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <InfoIcon color="blue.500" />
              <Text>Assigned Makeup Session</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedAssignment && (
              <VStack align="stretch" spacing={3}>
                <Box bg="blue.50" p={4} borderRadius="md">
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <Text fontWeight="bold" color="gray.600">Client:</Text>
                      <Text fontWeight="semibold">
                        {selectedAssignment.clientName}
                        {selectedAssignment.clientNickname && (
                          <Text as="span" color="gray.500"> "{selectedAssignment.clientNickname}"</Text>
                        )}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" color="gray.600">Date:</Text>
                      <Text>
                        {new Date(selectedAssignment.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" color="gray.600">Time:</Text>
                      <Text>{selectedAssignment.time}</Text>
                    </HStack>
                    {selectedAssignment.makeupFor && (
                      <HStack>
                        <Text fontWeight="bold" color="gray.600">Makeup for:</Text>
                        <Text>
                          {new Date(selectedAssignment.makeupFor).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  This slot has been assigned and cannot be modified.
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onAssignmentModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MakeupSessionAvailability;

